/*
 * Bülowsen – let JavaScript til bw-*-sektionerne.
 * Header (scroll, mobilmenu, kurvtæller), videohero, accordions, nyhedsbrev, skip-link og måling.
 * Alle komponenter er custom elements, så de genstarter af sig selv i Theme Editor.
 */
(() => {
  if (window.__bwLoaded) return;
  window.__bwLoaded = true;

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* privat vindue eller blokeret lager */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {
        /* ignorer */
      }
    },
  };

  /**
   * Måling. Udsender en custom event til Shopify Customer Events (web pixels),
   * som respekterer cookiesamtykke, og en DOM-event som andre værktøjer kan lytte på.
   */
  const track = (name, data = {}) => {
    if (!name) return;
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (e) {
      /* ignorer */
    }
    document.dispatchEvent(new CustomEvent('bw:track', { detail: { name, ...data } }));
  };
  window.bwTrack = track;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-track]') : null;
    if (target) track(target.dataset.track, { href: target.getAttribute('href') || '' });
  });

  /* Skip-link: efter heroen på forsiden, til købspanelet på produktsider. */
  const updateSkipLink = () => {
    const skip = document.querySelector('.skip-to-content-link');
    if (!skip) return;
    const afterHero = document.getElementById('bw-after-hero');
    const buy = document.getElementById('bw-buy');
    if (afterHero) {
      skip.setAttribute('href', '#bw-after-hero');
    } else if (buy) {
      skip.setAttribute('href', '#bw-buy');
      if (buy.dataset.skipLabel) skip.textContent = buy.dataset.skipLabel;
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateSkipLink, { once: true });
  } else {
    updateSkipLink();
  }

  /* Dwell scroller i .page-wrapper på desktop og i vinduet på mobil. */
  const scrollPosition = () => {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
  };

  /* Header: transparent på forsiden, solid efter scroll_threshold. Mobilmenu i <dialog>. */
  class BwHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      if (this.hasAttribute('data-transparent')) {
        const threshold = Number(this.dataset.threshold || 40);
        const update = () => this.classList.toggle('is-scrolled', scrollPosition() > threshold);
        update();
        window.addEventListener('scroll', update, { passive: true, signal });
        document.querySelector('.page-wrapper')?.addEventListener('scroll', update, { passive: true, signal });
      }

      /* Kurvtæller: bw-product.js sender bw:cart efter hver kurvændring. */
      document.addEventListener(
        'bw:cart',
        (event) => {
          const count = Number(event.detail?.count) || 0;
          const link = this.querySelector('[data-header-cart]');
          const counter = this.querySelector('[data-header-cart-count]');
          if (counter) {
            counter.textContent = `(${count})`;
            counter.hidden = count === 0;
          }
          if (link && this.dataset.cartMode === 'with_items') link.hidden = count === 0;
        },
        { signal }
      );

      /* Temaets egen kurvkode kan skrive "(0)" i tælleren uden at skjule den, fx når kurven tømmes i skuffen. */
      const counter = this.querySelector('[data-header-cart-count]');
      if (counter) {
        const sync = () => {
          const empty = /^\(?0\)?$/.test(counter.textContent.trim());
          if (counter.hidden !== empty) counter.hidden = empty;
        };
        const observer = new MutationObserver(sync);
        observer.observe(counter, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
        signal.addEventListener('abort', () => observer.disconnect());
      }

      this.dialog = this.querySelector('dialog');
      this.toggle = this.querySelector('[data-menu-open]');
      if (!this.dialog || !this.toggle) return;

      this.toggle.addEventListener('click', () => this.open(), { signal });
      this.dialog.querySelector('[data-menu-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener(
        'close',
        () => {
          this.toggle.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('bw-scroll-lock');
          this.toggle.focus({ preventScroll: true });
        },
        { signal }
      );
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog || (event.target instanceof Element && event.target.closest('a'))) {
            this.dialog.close();
          }
        },
        { signal }
      );
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.dialog.showModal();
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('bw-scroll-lock');
      this.dialog.querySelector('[data-menu-close]')?.focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('bw-scroll-lock');
    }
  }

  /**
   * Videohero: ingen video ved reduceret bevægelse eller Save-Data. Pausevalget huskes i sessionen.
   * Den optimerede original prøves først; fejler den, bruges Shopifys egen udgave, og fejler også den,
   * står posteren alene uden pauseknap.
   */
  class BwHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-hero-toggle]');
      if (!this.video) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const d = this.video.dataset;
      const useMobile = Boolean(isMobile && d.srcMobile) || !d.srcDesktop;
      const src = useMobile ? d.srcMobile : d.srcDesktop;
      this.fallback = (useMobile ? d.srcMobileFallback : d.srcDesktopFallback) || '';

      if (reduceMotion || saveData || !src) {
        this.fail();
        return;
      }

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.video.addEventListener('error', () => this.onError(), { signal });
      this.video.addEventListener(
        'playing',
        () => {
          this.classList.add('is-playing');
          this.setState(true);
        },
        { signal }
      );
      this.video.addEventListener('pause', () => this.setState(false), { signal });
      this.button?.addEventListener('click', () => this.toggleVideo(), { signal });

      this.video.src = src;

      if (storage.get('bw-hero-paused') === '1') {
        this.video.preload = 'metadata';
        this.setState(false);
      } else {
        this.play();
      }
    }

    onError() {
      if (!this.video) return;
      if (this.fallback) {
        const next = this.fallback;
        this.fallback = '';
        this.video.src = next;
        if (storage.get('bw-hero-paused') !== '1') this.play();
        return;
      }
      this.fail();
    }

    async play() {
      try {
        await this.video.play();
      } catch (e) {
        /* Autoplay blokeret: posteren står, og knappen tilbyder afspilning. */
        if (this.video) this.setState(false);
      }
    }

    toggleVideo() {
      if (!this.video) return;
      if (this.video.paused) {
        storage.set('bw-hero-paused', '0');
        this.play();
      } else {
        storage.set('bw-hero-paused', '1');
        this.video.pause();
      }
    }

    setState(playing) {
      if (!this.button) return;
      this.button.hidden = false;
      this.button.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
      this.button.dataset.state = playing ? 'playing' : 'paused';
      const text = this.button.querySelector('[data-hero-toggle-text]');
      if (text) text.textContent = playing ? this.dataset.textPause : this.dataset.textPlay;
    }

    fail() {
      this.video?.remove();
      this.video = null;
      if (this.button) this.button.hidden = true;
      this.classList.remove('is-playing');
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }

  /* Accordions (footer og specifikationer): kun på mobil. Paneler er hidden, når de er lukkede. */
  class BwAccordions extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mq = window.matchMedia('(max-width: 749px)');
      this.buttons = Array.from(this.querySelectorAll('[data-accordion-toggle]'));
      this.buttons.forEach((button) => button.addEventListener('click', () => this.toggle(button), { signal }));
      this.mq.addEventListener('change', () => this.sync(), { signal });
      this.sync();
    }

    panelFor(button) {
      return document.getElementById(button.getAttribute('aria-controls'));
    }

    sync() {
      for (const button of this.buttons) {
        const panel = this.panelFor(button);
        if (!panel) continue;
        panel.hidden = this.mq.matches ? button.getAttribute('aria-expanded') !== 'true' : false;
      }
    }

    toggle(button) {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      const panel = this.panelFor(button);
      if (panel) panel.hidden = open;
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }

  /* Nyhedsbrev: klientvalidering med aria-invalid og status i role="status". */
  class BwNewsletter extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.status = this.querySelector('[data-newsletter-status]');
      if (!this.form || !this.input) return;

      /* Egen validering erstatter browserens boble, så fejlen står i status-feltet. */
      this.form.noValidate = true;

      /* Shopify svarer for alle kundeformularer på siden. Hvis svaret hørte til en anden formular
         (fx standerventelisten), vises det ikke her. */
      const lastForm = storage.get('bw-last-form');
      const state = this.status?.dataset.state;
      if (state && lastForm && lastForm !== 'newsletter') {
        this.clearError();
      } else if (state === 'success' && !this.dataset.tracked) {
        this.dataset.tracked = 'true';
        track('nyhedsbrev_tilmeldt');
        storage.remove('bw-last-form');
      }

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form.addEventListener(
        'submit',
        (event) => {
          if (this.isValid()) {
            storage.set('bw-last-form', 'newsletter');
            return;
          }
          event.preventDefault();
          this.showError();
          this.input.focus();
        },
        { signal }
      );
      this.input.addEventListener(
        'input',
        () => {
          if (this.input.getAttribute('aria-invalid') === 'true' && this.isValid()) this.clearError();
        },
        { signal }
      );
    }

    isValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.input.value.trim());
    }

    showError() {
      this.input.setAttribute('aria-invalid', 'true');
      if (this.status) {
        this.status.textContent = this.dataset.errorText || '';
        this.status.dataset.state = 'error';
      }
    }

    clearError() {
      this.input.removeAttribute('aria-invalid');
      if (this.status) {
        this.status.textContent = '';
        delete this.status.dataset.state;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  define('bw-header', BwHeader);
  define('bw-hero', BwHero);
  define('bw-accordions', BwAccordions);
  define('bw-newsletter', BwNewsletter);
})();
